package com.partejar.app.feature.workspace.presentation

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.partejar.app.core.session.SessionCoordinator
import com.partejar.app.core.session.SessionDestination
import com.partejar.app.feature.workspace.data.SessionBootstrapDto
import com.partejar.app.feature.workspace.data.UserWorkspace
import com.partejar.app.feature.workspace.data.WorkspaceRepository
import com.partejar.app.feature.entitlements.data.CommercialConstraintException
import com.partejar.app.feature.entitlements.presentation.EntitlementCoordinator
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

sealed interface WorkspaceFormState {
    data object Idle : WorkspaceFormState
    data object Submitting : WorkspaceFormState
    data class Success(val workspace: UserWorkspace) : WorkspaceFormState
    data class Error(val message: String) : WorkspaceFormState
}

class WorkspaceViewModel(
    private val repository: WorkspaceRepository,
    private val sessionCoordinator: SessionCoordinator,
    private val entitlementCoordinator: EntitlementCoordinator? = null
) : ViewModel() {
    val bootstrap: StateFlow<SessionBootstrapDto?> = sessionCoordinator.bootstrap

    private val _formState = MutableStateFlow<WorkspaceFormState>(WorkspaceFormState.Idle)
    val formState: StateFlow<WorkspaceFormState> = _formState.asStateFlow()

    fun currentWorkspace(): UserWorkspace? = sessionCoordinator.currentWorkspace()

    fun select(workspaceId: Long) = sessionCoordinator.select(workspaceId)

    fun create(name: String) = submit(name) { normalizedName ->
        repository.create(normalizedName).also {
            sessionCoordinator.upsert(it, select = true)
            entitlementCoordinator?.onSessionReady()
        }
    }

    fun activateFreeAndCreate(name: String) = submit(name) { normalizedName ->
        entitlementCoordinator?.activateFree()?.getOrThrow()
            ?: throw IllegalStateException("Ativação do plano indisponível")
        repository.create(normalizedName).also {
            sessionCoordinator.upsert(it, select = true)
            entitlementCoordinator.onSessionReady()
        }
    }

    fun update(workspaceId: Long, name: String) = submit(name) { normalizedName ->
        repository.update(workspaceId, normalizedName).also { sessionCoordinator.upsert(it, select = false) }
    }

    fun refreshSession(onResolved: (SessionDestination) -> Unit) {
        viewModelScope.launch {
            try {
                onResolved(sessionCoordinator.resolve())
            } catch (error: CancellationException) {
                throw error
            } catch (_: Throwable) {
                // The caller remains on the current screen and can retry navigation.
            }
        }
    }

    fun logout() {
        viewModelScope.launch {
            sessionCoordinator.logout()
        }
    }

    fun clearFormState() {
        _formState.value = WorkspaceFormState.Idle
    }

    private fun submit(name: String, operation: suspend (String) -> UserWorkspace) {
        val normalizedName = name.trim()
        if (normalizedName.isEmpty()) {
            _formState.value = WorkspaceFormState.Error("Informe o nome do espaço.")
            return
        }
        _formState.value = WorkspaceFormState.Submitting
        viewModelScope.launch {
            _formState.value = try {
                WorkspaceFormState.Success(operation(normalizedName))
            } catch (error: CancellationException) {
                throw error
            } catch (error: CommercialConstraintException) {
                WorkspaceFormState.Error(commercialMessage(error.code))
            } catch (_: Throwable) {
                WorkspaceFormState.Error("Não foi possível salvar o espaço. Tente novamente.")
            }
        }
    }
}

internal fun commercialMessage(code: String): String = when (code) {
    "LIMITE_WORKSPACES_ATINGIDO" -> "Seu plano atingiu o limite de espaços próprios."
    "PLANO_NAO_ATIVO" -> "Ative o plano Free para criar seu espaço."
    "LIMITE_MEMBROS_ATINGIDO" -> "O plano deste espaço atingiu o limite de membros."
    "LIMITE_PACIENTES_ATINGIDO" -> "O plano deste espaço atingiu o limite de pacientes."
    else -> "Não foi possível concluir esta ação agora."
}
